import type { PortalNotification } from "../../data/portalQueries";
import {
  SETTINGS_ACCOUNT,
  SETTINGS_MANAGEMENT,
  SETTINGS_SECURITY,
  SETTINGS_SESSIONS
} from "../../lib/overviewDestination";

/**
 * Where a row on Notiser leads, when it leads anywhere.
 *
 * A request goes to the request itself, by the tenant-local number the row carries. Chat, documents
 * and outages go to their screens.
 *
 * An outage notice leads to Översikt since build round 4 (2026-09-27). It led to `/status` from the
 * day that page was built, and the page is gone: the website's state, a confirmed outage's cause and
 * the latest disruption are in Översikt's Webbplatsen box (plan.md section 5.6, 2026-09-26).
 *
 * **The subject is asked about before the category, and 2026-09-12 is why.** `security` is not one
 * kind of notice: `public.notifications_category_check` allows five categories and a client write
 * gateway may only ever write that one, so the secret feature's "En hemlighet väntar på er" is a
 * `security` row for a policy's reason rather than because it is about a credential.
 *
 * **Security rows open the section of Inställningar they are about**, since the second build round
 * of 2026-09-25 split Inställningar into one address per section. Until then every security row led
 * to `/settings`, one long page, and only Översikt split them (`app/lib/overviewDestination.ts`).
 * The split here follows the same `event_type`s: a sign-in on a new device opens Inloggningar, a
 * password or a second factor opens Lösenord och tvåfaktor, a new sign-in address opens Namn och
 * inloggning, and the management agreement opens Förvaltning. The section addresses are Översikt's
 * own constants, so the two screens cannot drift apart. No notice about a change to who has access
 * exists today, so nothing leads to Personer med åtkomst yet; the day one is written it gets a line
 * here and in Översikt's map together.
 *
 * Anything else about the account, and a row whose `event_type` has not been read yet or could not
 * be, still opens `/settings`, which is where every one of them led before. That fallback is this
 * screen's and not Översikt's: a history screen may send a reader to the list of sections, a to-do
 * list may not guess.
 */

// A `Map` rather than an object literal, so an `event_type` of "constructor" finds nothing instead
// of a function off `Object.prototype`.
const SECURITY_DESTINATION = new Map<string, string>([
  ["new_device", SETTINGS_SESSIONS],
  ["password_changed", SETTINGS_SECURITY],
  ["email_changed", SETTINGS_ACCOUNT],
  ["management_reactivated", SETTINGS_MANAGEMENT],
  ["management_cancellation_requested", SETTINGS_MANAGEMENT],
  ["management_cancellation_withdrawn", SETTINGS_MANAGEMENT],
  ["management_termination_started", SETTINGS_MANAGEMENT],
  ["management_termination_withdrawn", SETTINGS_MANAGEMENT],
  ["management_ended", SETTINGS_MANAGEMENT],
  // Without a subject id there is no transfer to open, so the secret screen itself is the answer.
  ["secret_waiting", "/secrets"],
  ["secret_viewed", "/secrets"]
]);

export type NotificationTarget = Pick<
  PortalNotification,
  "category" | "request_number" | "subject_type" | "subject_id"
> & { event_type?: string | null };

function securityDestination(event: string | null | undefined): string {
  const type = event ?? "";
  if (type.startsWith("mfa_")) {
    return SETTINGS_SECURITY;
  }
  return SECURITY_DESTINATION.get(type) ?? "/settings";
}

/**
 * Where Petrovic's own notices lead, which only an admin account receives. `null` for anything else.
 *
 * Asked before everything in `notificationDestination`, and build round 23 (2026-10-08) is why: a
 * client's new request or answer reaches Petrovic as a `client_request` row that carries the
 * request's number, and the number alone would have sent him to the client's `/requests/<n>`, a page
 * an admin account has no tenant for. Petrovic's page for a request is addressed by its id, which
 * the row carries as `subject_id`. A row without one opens Inkorg, where every client's waiting
 * request is listed.
 *
 * A client's publish opens Inkorg, where its notice is a row of its own until it is marked seen
 * (build round 19), and a warning opens Drift like a critical alarm does.
 */
export function adminNoticeDestination(item: NotificationTarget): string | null {
  switch (item.category) {
    // 2026-09-22. Inkorg since 2026-09-24, when the inbox left `/admin/clients` for a page of its own.
    case "client_message":
      return "/admin/inbox";
    case "client_request":
      return item.subject_type === "request" && item.subject_id
        ? `/admin/requests/${item.subject_id}`
        : "/admin/inbox";
    case "client_publish":
      return "/admin/inbox";
    // 2026-09-23 for the critical alarm, build round 23 for the warning.
    case "operator_alert":
    case "operator_warning":
      return "/admin/operations";
    default:
      return null;
  }
}

export function notificationDestination(item: NotificationTarget): string | null {
  const admin = adminNoticeDestination(item);
  if (admin !== null) {
    return admin;
  }
  if (item.request_number !== null) {
    return `/requests/${item.request_number}`;
  }
  if (item.subject_type === "one_time_secret_transfer" && item.subject_id) {
    return `/secrets/${item.subject_id}`;
  }
  switch (item.category) {
    case "chat_reply":
      return "/chat";
    case "document":
      return "/documents";
    case "report":
      return "/reports";
    case "outage":
      return "/overview";
    case "security":
      return securityDestination(item.event_type);
    // Petrovic's own categories (`client_message`, `client_request`, `client_publish`,
    // `operator_warning`, `operator_alert`) were answered by `adminNoticeDestination` above.
    default:
      return null;
  }
}
